// Owner's "Activity" panel: who did what and when on a form, newest first,
// with a filter by action type and "Load more" paging. Read-only.

import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "./utils/api";
import { describeActivity, activityWho, ACTIVITY_FILTERS } from "./utils/activityText";
import { formatIndiaDateTime } from "./utils/indiaTime";
import "./ActivityPanel.css";

// India time, whatever the viewer's device is set to.
const formatWhen = (value) => formatIndiaDateTime(value);

const ActivityPanel = ({ formId, onClose }) => {
    const [action, setAction] = useState("");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async (pageToLoad, actionFilter, append) => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({ page: String(pageToLoad) });
            if (actionFilter) params.set("action", actionFilter);
            const res = await apiFetch(`/api/form_builder/activity/${formId}?${params.toString()}`);
            if (!res.ok) throw new Error(res.status === 404 ? "Only the form owner can see its activity." : "Could not load activity.");
            const data = await res.json();
            setItems(prev => (append ? [...prev, ...data.items] : data.items));
            setHasMore(!!data.hasMore);
            setPage(pageToLoad);
        } catch (err) {
            setError(err.message || "Could not load activity.");
        } finally {
            setLoading(false);
        }
    }, [formId]);

    useEffect(() => {
        if (formId) load(1, action, false);
    }, [formId, action, load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div className="activity-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="activity-panel" role="dialog" aria-modal="true" aria-labelledby="activity-title">
                <div className="activity-head">
                    <h5 id="activity-title">Activity</h5>
                    <button type="button" className="activity-close" aria-label="Close" onClick={onClose}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <label className="activity-filter">
                    Show
                    <select value={action} onChange={(e) => setAction(e.target.value)}>
                        {ACTIVITY_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                </label>

                <div className="activity-list">
                    {error && <div className="activity-empty">{error}</div>}
                    {!error && !loading && items.length === 0 && (
                        <div className="activity-empty">No activity yet.</div>
                    )}
                    {items.map(item => (
                        <div className="activity-item" key={item.id}>
                            <div className="activity-avatar" aria-hidden="true">
                                {activityWho(item).charAt(0).toUpperCase()}
                            </div>
                            <div className="activity-text">
                                <div>
                                    <strong>{activityWho(item)}</strong> {describeActivity(item)}
                                </div>
                                <div className="activity-when">{formatWhen(item.created_at)}</div>
                            </div>
                        </div>
                    ))}
                    {loading && <div className="activity-empty">Loading...</div>}
                </div>

                {hasMore && !loading && (
                    <button type="button" className="activity-more" onClick={() => load(page + 1, action, true)}>
                        Load more
                    </button>
                )}
            </div>
        </div>
    );
};

export default ActivityPanel;
